import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  ListSubheader,
} from '@mui/material';
import { Menu as MenuIcon, Close } from '@mui/icons-material';
import { ZAROR_LOGO_PATH } from '../utils/brandAssets';
import {
  HEADER_CATEGORY_FALLBACK,
  categoriesForHomeRow1,
  categoriesForHomeRow2,
} from '../utils/homeCategoryRows';

const NAV_FONT_SIZE = '15px';
const NAV_COLOR = '#ffffff';
const HEADER_BG = 'rgba(0, 0, 0, 0.62)';
const HEADER_BORDER = 'rgba(255, 255, 255, 0.08)';
const HEADER_GLOW = '0 1px 12px rgba(0, 0, 0, 0.35)';

const navButtonSx = {
  color: NAV_COLOR,
  textTransform: 'none',
  fontWeight: 400,
  fontSize: NAV_FONT_SIZE,
  letterSpacing: 'normal',
  lineHeight: 1.2,
  px: 1,
  py: 0.375,
  minHeight: 'unset',
  '&:hover': {
    backgroundColor: 'transparent',
    color: NAV_COLOR,
    opacity: 0.75,
  },
};

const navMenuItemSx = {
  fontSize: NAV_FONT_SIZE,
  color: NAV_COLOR,
  py: 1.25,
  px: 2.5,
  minHeight: 48,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.45)',
  },
};

const navListItemTextProps = {
  primaryTypographyProps: { fontSize: NAV_FONT_SIZE, color: NAV_COLOR },
};

const dropdownPaperSx = {
  backgroundColor: 'rgba(18, 18, 18, 0.94)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  border: `1px solid ${HEADER_BORDER}`,
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.55)',
  minWidth: 220,
  borderRadius: '12px',
  mt: 0.75,
};

const ZarorHeader = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [kitAnchorEl, setKitAnchorEl] = useState(null);
  const [trainingAnchorEl, setTrainingAnchorEl] = useState(null);
  const kitCloseTimer = useRef(null);
  const trainingCloseTimer = useRef(null);
  const kitBtnRef = useRef(null);
  const trainingBtnRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  const kitBuilderCategories = useMemo(
    () => categoriesForHomeRow1(categories, catLoading, HEADER_CATEGORY_FALLBACK),
    [categories, catLoading]
  );
  const trainingGearCategories = useMemo(
    () => categoriesForHomeRow2(categories, catLoading),
    [categories, catLoading]
  );

  useEffect(() => {
    let cancelled = false;
    setCatLoading(true);
    axios
      .get(`${apiUrl}/categories`)
      .then((res) => {
        if (!cancelled) setCategories(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setCategories(HEADER_CATEGORY_FALLBACK);
      })
      .finally(() => {
        if (!cancelled) setCatLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const clearCloseTimer = (timerRef) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const openKitMenu = (el) => {
    clearCloseTimer(kitCloseTimer);
    clearCloseTimer(trainingCloseTimer);
    setTrainingAnchorEl(null);
    setKitAnchorEl(el);
  };

  const scheduleCloseKitMenu = () => {
    clearCloseTimer(kitCloseTimer);
    kitCloseTimer.current = setTimeout(() => setKitAnchorEl(null), 160);
  };

  const openTrainingMenu = (el) => {
    clearCloseTimer(trainingCloseTimer);
    clearCloseTimer(kitCloseTimer);
    setKitAnchorEl(null);
    setTrainingAnchorEl(el);
  };

  const scheduleCloseTrainingMenu = () => {
    clearCloseTimer(trainingCloseTimer);
    trainingCloseTimer.current = setTimeout(() => setTrainingAnchorEl(null), 160);
  };

  const mobileMenu = (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={handleDrawerClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          boxSizing: 'border-box',
          zIndex: (theme) => theme.zIndex.drawer,
          backgroundColor: 'rgba(12, 12, 12, 0.97)',
          color: NAV_COLOR,
        },
        zIndex: (theme) => theme.zIndex.drawer,
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: NAV_COLOR }}>
            Zaror Sports
          </Typography>
          <IconButton
            aria-label="close menu"
            onClick={handleDrawerClose}
            sx={{
              color: NAV_COLOR,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2, borderColor: HEADER_BORDER }} />
        <List>
          <ListItem button component={Link} to="/" onClick={handleDrawerClose}>
            <ListItemText primary="Home" {...navListItemTextProps} />
          </ListItem>
          <ListItem button component={Link} to="/about" onClick={handleDrawerClose}>
            <ListItemText primary="About Us" {...navListItemTextProps} />
          </ListItem>
          <ListSubheader
            sx={{
              bgcolor: 'transparent',
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 700,
              fontSize: NAV_FONT_SIZE,
            }}
          >
            Kit Builder
          </ListSubheader>
          {kitBuilderCategories.map((c) => (
            <ListItem
              button
              key={`kit-${c.id}`}
              component={Link}
              to={`/${c.slug}`}
              onClick={handleDrawerClose}
              sx={{ pl: 4 }}
            >
              <ListItemText primary={c.name} {...navListItemTextProps} />
            </ListItem>
          ))}
          <ListSubheader
            sx={{
              bgcolor: 'transparent',
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 700,
              fontSize: NAV_FONT_SIZE,
            }}
          >
            Training Gears
          </ListSubheader>
          {trainingGearCategories.length > 0 ? (
            trainingGearCategories.map((c) => (
              <ListItem
                button
                key={`train-${c.id}`}
                component={Link}
                to={`/${c.slug}`}
                onClick={handleDrawerClose}
                sx={{ pl: 4 }}
              >
                <ListItemText primary={c.name} {...navListItemTextProps} />
              </ListItem>
            ))
          ) : (
            <ListItem sx={{ pl: 4 }}>
              <ListItemText
                primary="None yet — add categories to More Sports in admin"
                {...navListItemTextProps}
              />
            </ListItem>
          )}
          <ListItem button component={Link} to="/tracksuits" onClick={handleDrawerClose}>
            <ListItemText primary="Tracksuits" {...navListItemTextProps} />
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar
        position="sticky"
        color="transparent"
        elevation={0}
        sx={{
          backgroundColor: HEADER_BG,
          backdropFilter: 'blur(24px) saturate(140%) brightness(0.72)',
          WebkitBackdropFilter: 'blur(24px) saturate(140%) brightness(0.72)',
          borderBottom: `1px solid ${HEADER_BORDER}`,
          boxShadow: HEADER_GLOW,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              py: 0,
              minHeight: '44px',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {!isMobile ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  width: '100%',
                }}
              >
                <Link to="/" style={{ textDecoration: 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img
                      src={ZAROR_LOGO_PATH}
                      alt="Zaror Sports Logo"
                      style={{
                        height: '38px',
                        width: 'auto',
                        marginRight: '10px',
                      }}
                    />
                  </Box>
                </Link>

                <Button component={Link} to="/" sx={navButtonSx}>
                  Home
                </Button>
                <Button component={Link} to="/about" sx={navButtonSx}>
                  About Us
                </Button>
                <Box
                  onMouseEnter={() => kitBtnRef.current && openKitMenu(kitBtnRef.current)}
                  onMouseLeave={scheduleCloseKitMenu}
                  sx={{ display: 'inline-block' }}
                >
                  <Button
                    ref={kitBtnRef}
                    sx={navButtonSx}
                    aria-haspopup="true"
                    aria-expanded={Boolean(kitAnchorEl)}
                    aria-controls="kit-builder-menu"
                  >
                    Kit Builder
                  </Button>
                  <Menu
                    id="kit-builder-menu"
                    anchorEl={kitAnchorEl}
                    open={Boolean(kitAnchorEl)}
                    onClose={() => setKitAnchorEl(null)}
                    MenuListProps={{
                      onMouseEnter: () => clearCloseTimer(kitCloseTimer),
                      onMouseLeave: scheduleCloseKitMenu,
                      sx: { py: 0.75 },
                    }}
                    PaperProps={{
                      onMouseEnter: () => clearCloseTimer(kitCloseTimer),
                      onMouseLeave: scheduleCloseKitMenu,
                      sx: dropdownPaperSx,
                    }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    disableAutoFocusItem
                    keepMounted
                  >
                    {kitBuilderCategories.length === 0 ? (
                      <MenuItem disabled sx={navMenuItemSx}>No categories</MenuItem>
                    ) : (
                      kitBuilderCategories.map((c) => (
                        <MenuItem
                          key={c.id}
                          component={Link}
                          to={`/${c.slug}`}
                          onClick={() => setKitAnchorEl(null)}
                          sx={navMenuItemSx}
                        >
                          {c.name}
                        </MenuItem>
                      ))
                    )}
                  </Menu>
                </Box>
                <Box
                  onMouseEnter={() => trainingBtnRef.current && openTrainingMenu(trainingBtnRef.current)}
                  onMouseLeave={scheduleCloseTrainingMenu}
                  sx={{ display: 'inline-block' }}
                >
                  <Button
                    ref={trainingBtnRef}
                    sx={navButtonSx}
                    aria-haspopup="true"
                    aria-expanded={Boolean(trainingAnchorEl)}
                    aria-controls="training-gears-menu"
                  >
                    Training Gears
                  </Button>
                  <Menu
                    id="training-gears-menu"
                    anchorEl={trainingAnchorEl}
                    open={Boolean(trainingAnchorEl)}
                    onClose={() => setTrainingAnchorEl(null)}
                    MenuListProps={{
                      onMouseEnter: () => clearCloseTimer(trainingCloseTimer),
                      onMouseLeave: scheduleCloseTrainingMenu,
                      sx: { py: 0.75 },
                    }}
                    PaperProps={{
                      onMouseEnter: () => clearCloseTimer(trainingCloseTimer),
                      onMouseLeave: scheduleCloseTrainingMenu,
                      sx: dropdownPaperSx,
                    }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    disableAutoFocusItem
                    keepMounted
                  >
                    {trainingGearCategories.length === 0 ? (
                      <MenuItem disabled sx={navMenuItemSx}>No sports in More Sports yet</MenuItem>
                    ) : (
                      trainingGearCategories.map((c) => (
                        <MenuItem
                          key={c.id}
                          component={Link}
                          to={`/${c.slug}`}
                          onClick={() => setTrainingAnchorEl(null)}
                          sx={navMenuItemSx}
                        >
                          {c.name}
                        </MenuItem>
                      ))
                    )}
                  </Menu>
                </Box>
                <Button component={Link} to="/tracksuits" sx={navButtonSx}>
                  Tracksuits
                </Button>
              </Box>
            ) : (
              <>
                <Link to="/" style={{ textDecoration: 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img
                      src={ZAROR_LOGO_PATH}
                      alt="Zaror Sports Logo"
                      style={{
                        height: '34px',
                        width: 'auto',
                      }}
                    />
                  </Box>
                </Link>

                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                  sx={{
                    color: NAV_COLOR,
                    position: 'absolute',
                    right: 0,
                    p: 0.5,
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      {mobileMenu}
    </>
  );
};

export default ZarorHeader;

